import { DB_FIELD_MAX_LENGTH } from "@selector/shared/settings";
import { Component, type ReactElement, type ReactNode } from "react";
import { connect } from "react-redux";
import {
  change,
  type FieldState,
  Form,
  formValueSelector,
  type FormWarnings,
  getFormMeta,
  type InjectedFormProps,
  reduxForm,
  SubmissionError,
} from "redux-form";
import { Collapse, HStack, InfoToggletip, LoadingSpinner, TooltipIntent } from "widgets";

import { type ISelectOption, simpleDropdownFieldValidation, simpleFieldValidation } from "@/app-utils/form.js";
import {
  isAutomaticRepresenterAttribute,
  isDynamicTerm,
  isFileAttribute,
  isRepresenterOrUsageAttribute,
  isScalarValue,
} from "@/app-utils/sample.js";
import { trimOrThrow } from "@/app-utils/string.js";
import type { Attribute, InputAttributeInput, VocabularyTerm } from "@/graphql.js";
import {
  createAttribute,
  loadAttributesForDecoration,
  modifyAttribute,
  onControlledVocabularyListChanged,
} from "@/redux/modules/sample-admin.js";
import { ensureRowIsVisible, reloadTableRows } from "@/redux/modules/table-data.js";
import type { IAppState } from "@/redux/reducer.js";
import { SelForm } from "@/ui-modules/core/form/layout/Form.js";

import {
  ATTRIBUTE_TYPE,
  ATTRIBUTE_TYPE_OPTIONS,
  COLUMN_SET,
  REPRESENTER_COLUMN,
  SAM_FILE_TYPE_OPTIONS,
} from "../../settings/settings.js";
import { SCRIPTING_MACRO, ScriptingLanguage } from "../core/forms/ScriptInputField.js";
import { Dialog } from "../core/overlays/Dialogs.js";
import CheckboxField, { CheckboxReduxFormField } from "../core/redux-form/CheckboxField.js";
import InputField, { InputReduxFormField } from "../core/redux-form/InputField.js";
import ReactSelectField, { ReactSelectReduxFormField } from "../core/redux-form/ReactSelectField.js";
import { ScriptInputReduxFormField } from "../core/redux-form/ScriptInputField.js";
import { FieldTitle } from "../playbooks/FieldTitle.js";
import ControlledValuesManager, { type IInputVocabularyTerm } from "./ControlledValuesManager.js";
import { sampleAttributesTableSpec } from "./SampleAttributesAdminPage.js";
import style from "./styles/AttributesAdmin.module.scss";

interface IFieldSettings {
  field?: string;
  attributeTypes?: number[];
  hint?: ReactElement;
}

// every UI field gets individual settings controlling its visibility
interface IFieldVisibilitySettings {
  attributeTypeField: IFieldSettings;
  attributeNameField: IFieldSettings;
  columnSetField: IFieldSettings;
  mandatoryField: IFieldSettings;
  multipleValuesField: IFieldSettings;
  isMultiValuesHeadField: IFieldSettings;
  modifiableField: IFieldSettings;
  editOnceField: IFieldSettings;
  disabledField: IFieldSettings;
  automaticField: IFieldSettings;
  groupNameField: IFieldSettings;
  minField: IFieldSettings;
  maxField: IFieldSettings;
  decimalsField: IFieldSettings;
  maxLengthField: IFieldSettings;
  patternField: IFieldSettings;
  patternHintField: IFieldSettings;
  formulaField: IFieldSettings;
  derivedSqlField: IFieldSettings;
  dynamicSqlField: IFieldSettings;
  idAccessorField: IFieldSettings;
  frozenField: IFieldSettings;
  fileTypeField: IFieldSettings;
  vocabularyTermsField: IFieldSettings;
  coloringRuleField: IFieldSettings;
}

interface IAddSampleAttributeDialogState {
  decorationAttributes: Attribute[];
  isLoadingAttributes: boolean;
  dataFetchingWarning: string;

  attributesUsedInFormula: Attribute[];
  attributesUsedInSql: Attribute[];
  attributesUsedInColoring: Attribute[];
}

export interface IAddSampleAttributeDialogOwnProps {
  attributeCreated?: (createdAttribute: Attribute) => void;
  onClose?: (fulfilled?: Attribute) => void;
  editingContext?: boolean;
  attributeToEdit?: Attribute;
}

interface IAddSampleAttributeDialogDispatchProps {
  createAttribute: (payload: InputAttributeInput) => Promise<Attribute>;
  modifyAttribute: (payload: InputAttributeInput) => Promise<Attribute>;
  onControlledVocabularyListChanged: (controlledValues: IInputVocabularyTerm[]) => void;
  reloadListOfAttributes: () => Promise<any>;
  loadAttributes: () => Promise<Attribute[]>;
  changeFieldValue: (fieldName, value) => void;
  ensureRowIdIsVisible: (rowId: number) => void;
}

interface IAddSampleAttributeDialogAppStateProps {
  controlledValues: IInputVocabularyTerm[];
  selectedAttributeTypeId: number;
  selectedColumnSetId: number;
  selectedAutomatic: boolean;
  selectedModifiable: boolean;
  selectedMultipleValues: boolean;
  enteredGroupName: string;
  initialValues: Partial<any>;
  fieldState: Record<string, FieldState>; // map by name
}

export interface IAddSampleAttributeDialogProps
  extends IAddSampleAttributeDialogAppStateProps,
    IAddSampleAttributeDialogDispatchProps,
    IAddSampleAttributeDialogOwnProps,
    InjectedFormProps<any, any> {}

function mapStateToProps(
  state: IAppState,
  ownProps: IAddSampleAttributeDialogOwnProps,
): IAddSampleAttributeDialogAppStateProps {
  let initialValues: any = {};

  if (ownProps.editingContext) {
    initialValues = {
      [AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_ATTRIBUTE_TYPE]: ATTRIBUTE_TYPE_OPTIONS.find(
        (option: ISelectOption) => ownProps.attributeToEdit.attributeType.id === option.value,
      ),
      [AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_AUTOMATIC]: ownProps.attributeToEdit.automatic,
      [AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_COLUMN_SET]:
        AddSampleAttributeImpl.columnSetOptions.find(
          (option: ISelectOption) => ownProps.attributeToEdit.columnSet.id === option.value,
        ),
      [AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_DECIMALS]: ownProps.attributeToEdit.decimals,
      [AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_DISABLED]: ownProps.attributeToEdit.disabled,
      [AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_EDITONCE]: ownProps.attributeToEdit.editOnce,
      [AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_FROZEN]: ownProps.attributeToEdit.frozen,
      [AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_GROUPNAME]: ownProps.attributeToEdit.groupName,
      [AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_IDACCESSOR]: ownProps.attributeToEdit.idAccessor,
      [AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_MANDATORY]: ownProps.attributeToEdit.mandatory,
      [AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_MAXLENGTH]: ownProps.attributeToEdit.maxLength,
      [AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_MAX]: ownProps.attributeToEdit.maxValue,
      [AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_MIN]: ownProps.attributeToEdit.minValue,
      [AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_MODIFIABLE]: ownProps.attributeToEdit.modifiable,
      [AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_ATTRIBUTE_NAME]: ownProps.attributeToEdit.attributeName,
      [AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_PATTERN]: ownProps.attributeToEdit.pattern,
      [AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_PATTERNHINT]: ownProps.attributeToEdit.patternHint,
      [AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_FILE_TYPE]: ownProps.attributeToEdit.fileType?.id
        ? SAM_FILE_TYPE_OPTIONS.find((o) => o.value === ownProps.attributeToEdit.fileType.id)
        : null,
      [AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_FORMULA]: ownProps.attributeToEdit.formulaText,
      [AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_COLORINGRULE]: ownProps.attributeToEdit.coloringRule,
      [AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_SQLSTATEMENT]: ownProps.attributeToEdit.sqlStatement,
      [AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_MULTIPLEVALUES]:
        ownProps.attributeToEdit.multipleValues,
      [AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_IS_MULTIVALUES_HEAD]: ownProps.attributeToEdit.isHead,
    };
  } else {
    initialValues = AddSampleAttributeImpl.initialValues;
  }

  const selectedAttributeType: ISelectOption = formValueSelector(AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_FORM_NAME)(
    state,
    AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_ATTRIBUTE_TYPE,
  );
  const selectedColumnSetId: ISelectOption = formValueSelector(AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_FORM_NAME)(
    state,
    AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_COLUMN_SET,
  );

  return {
    controlledValues: state.sampleAdmin.controlledValues,
    selectedAttributeTypeId: selectedAttributeType?.value,
    selectedColumnSetId: selectedColumnSetId?.value,
    selectedAutomatic: formValueSelector(AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_FORM_NAME)(
      state,
      AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_AUTOMATIC,
    ),
    selectedModifiable: formValueSelector(AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_FORM_NAME)(
      state,
      AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_MODIFIABLE,
    ),
    selectedMultipleValues: formValueSelector(AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_FORM_NAME)(
      state,
      AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_MULTIPLEVALUES,
    ),
    enteredGroupName: formValueSelector(AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_FORM_NAME)(
      state,
      AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_GROUPNAME,
    ),
    initialValues: initialValues,
    fieldState: getFormMeta(AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_FORM_NAME)(state),
  };
}

function mapDispatchToProps(dispatch): IAddSampleAttributeDialogDispatchProps {
  return {
    createAttribute: (payload: InputAttributeInput) => dispatch(createAttribute(payload)),
    modifyAttribute: (payload: InputAttributeInput) => dispatch(modifyAttribute(payload)),
    onControlledVocabularyListChanged: (controlledValues: IInputVocabularyTerm[]) =>
      dispatch(onControlledVocabularyListChanged(controlledValues)),
    reloadListOfAttributes: () => dispatch(reloadTableRows(sampleAttributesTableSpec, true)),
    loadAttributes: () => dispatch(loadAttributesForDecoration()),
    changeFieldValue: (fielName, value) =>
      dispatch(change(AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_FORM_NAME, fielName, value)),
    ensureRowIdIsVisible: (rowId: number) => dispatch(ensureRowIsVisible(sampleAttributesTableSpec, rowId)),
  };
}

class AddSampleAttributeImpl extends Component<IAddSampleAttributeDialogProps, IAddSampleAttributeDialogState> {
  private _isUnmounting = false;

  // These fields are the various UI fields (not DIRECTLY redux form fields).
  // We can't re-use the redux form fields because the SQLSTATEMENT redux form field is used in two UI fields.
  private ATTRIBUTE_TYPE_FIELD = "attributeTypeField";
  private ATTRIBUTE_NAME_FIELD = "attributeNameField";
  private COLUMN_SET_FIELD = "columnSetField";
  private MANDATORY_FIELD = "mandatoryField";
  private MODIFIABLE_FIELD = "modifiableField";
  private EDIT_ONCE_FIELD = "editOnceField";
  private MULTIPLE_VALUES_FIELD = "multipleValuesField";
  private IS_HEAD_FIELD = "isMultiValuesHeadField";
  private DISABLED_FIELD = "disabledField";
  private AUTOMATIC_FIELD = "automaticField";
  private GROUP_NAME_FIELD = "groupNameField";
  private MIN_FIELD = "minField";
  private MAX_FIELD = "maxField";
  private DECIMALS_FIELD = "decimalsField";
  private MAX_LENGTH_FIELD = "maxLengthField";
  private PATTERN_FIELD = "patternField";
  private PATTERN_HINT_FIELD = "patternHintField";
  public static FORMULA_FIELD = "formulaField";
  public static COLORING_RULE_FIELD = "coloringRuleField";
  private DERIVED_SQL_FIELD = "derivedSqlField"; // this...
  private DYNAMIC_SQL_FIELD = "dynamicSqlField"; // ...and this are using the same redux form field
  private ID_ACCESSOR_FIELD = "idAccessorField";
  private FILE_TYPE_FIELD = "fileTypeField";
  // private FROZEN_FIELD = 'frozenField'; // disabled until properly implemented
  private VOCABULARY_TERMS_FIELD = "vocabularyTermsField";

  public static ADD_SAMPLE_ATTRIBUTE_FORM_NAME = "addSampleAttribute";
  // redux form field names
  public static ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_ATTRIBUTE_NAME = "attributeName";
  public static ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_GROUPNAME = "groupName";
  public static ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_ATTRIBUTE_TYPE = "attributeType";
  public static ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_COLUMN_SET = "columnSet";
  public static ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_MANDATORY = "mandatory";
  public static ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_EDITONCE = "editOnce";
  public static ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_MODIFIABLE = "modifiable";
  public static ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_DISABLED = "disabled";
  public static ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_AUTOMATIC = "automatic";
  public static ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_FROZEN = "frozen";
  public static ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_FILE_TYPE = "fileType";
  public static ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_MIN = "minValue";
  public static ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_MAX = "maxValue";
  public static ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_DECIMALS = "decimals";
  public static ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_MAXLENGTH = "maxLength";
  public static ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_MULTIPLEVALUES = "multipleValues";
  public static ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_IS_MULTIVALUES_HEAD = "isHead";
  public static ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_PATTERN = "pattern";
  public static ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_PATTERNHINT = "patternHint";
  public static ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_FORMULA = "formula";
  public static ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_COLORINGRULE = "coloringRule";
  public static ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_SQLSTATEMENT = "sqlStatement";
  public static ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_IDACCESSOR = "idAccessor";

  static initialValues = {
    [AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_MANDATORY]: false,
    [AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_DISABLED]: false,
    [AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_AUTOMATIC]: false,
    [AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_EDITONCE]: false,
    [AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_FROZEN]: false,
    [AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_MODIFIABLE]: true,
  };

  // The settings for every UI field.
  // Presence of an attributeTypes array indicates that the field is not visible for every attribute type.
  static fieldVisibilitySettings: IFieldVisibilitySettings = {
    attributeTypeField: {
      field: AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_ATTRIBUTE_TYPE,
    },
    attributeNameField: {
      field: AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_ATTRIBUTE_NAME,
    },
    columnSetField: {
      field: AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_COLUMN_SET,
      hint: (
        <>
          Every attribute must be assigned to a Column Set. Use "Annotation" if this attribute is used to describe the
          sample. Use "Analytical Data" if this attribute represents data that is collected for this sample. Use
          "Parent" if this attribute is used to describe a grouping of samples.
        </>
      ),
    },
    mandatoryField: {
      field: AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_MANDATORY,
      hint: (
        <>
          A mandatory attribute is an attribute that requires the user to enter a value. If the user is presented with a
          mandatory attribute on a form, a value must be provided or the user is not allowed to proceed.
        </>
      ),
    },
    modifiableField: {
      field: AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_MODIFIABLE,
      hint: (
        <>
          A modifiable attribute can be given a value within the application. If this is set to false, users will not be
          able to provide a value for this attribute.
        </>
      ),
    },
    editOnceField: {
      field: AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_EDITONCE,
      hint: (
        <>
          By selecting Edit Once option, users will be able to provide a value for this attribute. But after a value is
          entered, it cannot be edited.
        </>
      ),
    },
    multipleValuesField: {
      field: AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_MULTIPLEVALUES,
      hint: <>By selecting this option, users will be able to provide multiple values for this attribute.</>,
    },
    isMultiValuesHeadField: {
      field: AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_IS_MULTIVALUES_HEAD,
      hint: (
        <>
          By selecting this option, which is only available for multi value attributes with a group name, the attribute
          is used as pivot for aligned value tables.
        </>
      ),
    },
    disabledField: {
      field: AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_DISABLED,
      hint: (
        <>
          A disabled attribute will be present in the database, but is hidden everywhere in the user interface. This
          feature can be used to remove an attribute from the application without deleting any values associated with
          it.
        </>
      ),
    },
    automaticField: {
      field: AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_AUTOMATIC,
      hint: (
        <>
          An automatic attribute is an attribute that does not allow end-users to provide values. The values are always
          automatically provided by the system. For example, an attribute called "Sample ID" could be an automatic
          attribute where the system assigns values automatically for each sample based on a set of rules.
        </>
      ),
    },
    groupNameField: {
      field: AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_GROUPNAME,
      hint: (
        <>
          The Group Name will appear above an attribute when displayed in a table. Attributes with the same group name
          are grouped together in the column selection dialog and in summary pages. Leave this field empty if this
          attribute should not be grouped together with other attributes.
        </>
      ),
    },
    minField: {
      field: AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_MIN,
      attributeTypes: [ATTRIBUTE_TYPE.FREE_NUMERICAL],
    },
    maxField: {
      field: AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_MAX,
      attributeTypes: [ATTRIBUTE_TYPE.FREE_NUMERICAL],
    },
    decimalsField: {
      field: AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_DECIMALS,
      attributeTypes: [ATTRIBUTE_TYPE.FREE_NUMERICAL, ATTRIBUTE_TYPE.CALCULATED],
      hint: <>The number of digits shown after the decimal-sign.</>,
    },
    maxLengthField: {
      field: AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_MAXLENGTH,
      attributeTypes: [ATTRIBUTE_TYPE.FREE_TEXT, ATTRIBUTE_TYPE.HYPERLINK],
    },
    patternField: {
      field: AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_PATTERN,
      attributeTypes: [ATTRIBUTE_TYPE.FREE_TEXT],
      hint: (
        <>
          The regular expression defining the expected text formatting for the attribute value.
          <br />
          Since this is not human readable it is also possible to provide some hint for the user below.
        </>
      ),
    },
    patternHintField: {
      field: AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_PATTERNHINT,
      attributeTypes: [ATTRIBUTE_TYPE.FREE_TEXT],
    },
    formulaField: {
      field: AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_FORMULA,
      attributeTypes: [ATTRIBUTE_TYPE.CALCULATED],
      hint: (
        <>
          The formula can make use of javascript expressions and references to other attributes.
          <br />
          It returns the numeric value of the attribute.
          <br />
          <br />
          <strong>Mathematical operators:</strong> e.g. +, -, *, /, %.
          <br />
          <br />
          <strong>Constants:</strong> e.g. Math.PI, Math.E, Math.LN2, Math.SQRT1_2.
          <br />
          <br />
          <strong>Functions:</strong> e.g. Math.abs(x), Math.acos(x), Math.floor(x), Math.log(x), Math.pow(x).
          <br />
          <br />
          <strong>Attributes:</strong> Numeric attribute values of the same sample or parent type can be referenced via
          their attribute ID prefixed by the dollar $ sign. Use <i>Ctrl+Space</i> to select from a list of available
          attributes.
          <br />
          <br />
          <strong>Example:</strong> 0.3 * Math.pow(Math.E, $1000034).
        </>
      ),
    },
    coloringRuleField: {
      field: AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_COLORINGRULE,
      attributeTypes: [
        ATTRIBUTE_TYPE.CALCULATED,
        ATTRIBUTE_TYPE.FREE_NUMERICAL,
        ATTRIBUTE_TYPE.FREE_TEXT,
        ATTRIBUTE_TYPE.CONTROLLED_TEXT,
        ATTRIBUTE_TYPE.BOOLEAN,
        ATTRIBUTE_TYPE.DATE,
        ATTRIBUTE_TYPE.DYNAMIC_TERMS,
      ],
      hint: (
        <>
          The coloring rule can make use of javascript expressions and references to the same or other attributes.
          <br />
          It must return a color as HTML color code which will be visible in sample grids and in sample summaries.
          <br />
          <br />
          <strong>Attributes:</strong> Attribute values of the same sample or parent type can be referenced via their
          attribute ID prefixed by the dollar $ sign.
          <br />
          For referencing the value itself, you may use the <i>My Value</i> macro.
          <br />
          Hit <i>Ctrl+Space</i> to select from a list of available attributes and macros.
          <br />
          <br />
          <strong>Example:</strong> if ($1000034 &#62; 10) &#123;&#34;#FFAA00&#34;&#125; else
          &#123;&#34;#00FFAA&#34;&#125;
        </>
      ),
    },
    derivedSqlField: {
      field: AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_SQLSTATEMENT,
      attributeTypes: [ATTRIBUTE_TYPE.DERIVED_TERMS],
      hint: (
        <>
          The SQL select statement to get a value derived from some other controlled attribute.
          <br />
          Only basic SQL syntax is currently supported (e.g. no table alias, no inner join...)
          <br />
          The validation process will make sure that a corresponding database query can be executed.
        </>
      ),
    },
    dynamicSqlField: {
      field: AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_SQLSTATEMENT, // !!
      attributeTypes: [ATTRIBUTE_TYPE.DYNAMIC_TERMS],
      hint: (
        <>
          The SQL select statement to get the full (ID, NAME) list for the dynamically created vocabulary.
          <br />
          Only basic SQL syntax is currently supported (e.g. no table alias, no inner join...)
          <br />
          The validation process will make sure that a corresponding database query can be executed.
        </>
      ),
    },
    idAccessorField: {
      field: AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_IDACCESSOR,
      attributeTypes: [ATTRIBUTE_TYPE.DYNAMIC_TERMS],
      hint: (
        <>
          The accessor for the ID of the accessed entity. It describes where to get the actual entity using the ID.
          <br />
          The value should follow a specific pattern: "SCHEMA"."TABLE"."ID_ACCESSOR", and the validation process will
          make sure that a corresponding database query can be executed.
          <br />
          In case of ID accessors referring to table GEN_GENERIC_SEQ, the ID accessor has a special meaning to indicate
          the type of sequence to be retrieved. Use "NUS_ID" for contigs, "TCP_ID" for transcripts, "PRS_ID" for
          proteins or the default "GES_ID" if the exact sequence type can't be specified.
        </>
      ),
    },
    frozenField: {
      field: AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_FROZEN,
      attributeTypes: [ATTRIBUTE_TYPE.CONTROLLED_TEXT],
      hint: <>If frozen, the value list of the attribute is fixed and may not be changed.</>,
    },
    fileTypeField: {
      field: AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_FILE_TYPE,
      attributeTypes: [ATTRIBUTE_TYPE.FILE],
      hint: <>File attributes need a file type selection to specify allowed file assignments.</>,
    },
    vocabularyTermsField: {
      attributeTypes: [ATTRIBUTE_TYPE.CONTROLLED_TEXT],
    },
  };

  public static columnSetOptions: ISelectOption[] = [
    COLUMN_SET.COS_ANALYTICAL_ID,
    COLUMN_SET.COS_ANNOTATION_ID,
    COLUMN_SET.COS_PARENT_ID,
  ].map((columnSet) => ({ value: columnSet.id, label: columnSet.name }));

  constructor(props: IAddSampleAttributeDialogProps) {
    super(props);

    this.state = {
      decorationAttributes: [],
      isLoadingAttributes: true,
      dataFetchingWarning: null,
      attributesUsedInFormula: [],
      attributesUsedInColoring: [],
      attributesUsedInSql: [],
    };
  }

  // returns the hint for a UI field
  getFieldHint = (fieldSettingsKey: string): ReactElement => {
    return AddSampleAttributeImpl.fieldVisibilitySettings[fieldSettingsKey].hint;
  };

  // returns the related attribute types for a UI field
  static getFieldAttributeTypes = (fieldSettingsKey: string): number[] => {
    return AddSampleAttributeImpl.fieldVisibilitySettings[fieldSettingsKey].attributeTypes;
  };

  // returns the redux form field name for a UI field
  getFieldFormField = (fieldSettingsKey: string): string => {
    return AddSampleAttributeImpl.fieldVisibilitySettings[fieldSettingsKey].field;
  };

  isEvaluatedType = (atyId: number): boolean => {
    return atyId === ATTRIBUTE_TYPE.CALCULATED || atyId === ATTRIBUTE_TYPE.DERIVED_TERMS;
    // attribute types which depend on some other attributes to create their value.
  };

  static filterAttributesForFormula = (attributes: Attribute[], self: Attribute): Attribute[] => {
    return attributes
      .filter((attribute: Attribute) => isScalarValue(attribute) && !isFileAttribute(attribute))
      .filter((attribute: Attribute) => attribute.id !== self?.id); // exclude self from the formula
  };

  static filterAttributesForColoring = (attributes: Attribute[]): Attribute[] => {
    return attributes.filter(
      (attribute: Attribute) =>
        isScalarValue(attribute) && !isFileAttribute(attribute) && !isRepresenterOrUsageAttribute(attribute),
    );
  };

  static filterAttributesForDerivedValues = (attributes: Attribute[]): Attribute[] => {
    return attributes.filter((attribute: Attribute) => isDynamicTerm(attribute));
  };

  componentWillUnmount(): void {
    this._isUnmounting = true;
  }

  componentDidMount() {
    // make sure the vocabulary terms are stored in the redux state if they are provided for editing
    if (this.props.attributeToEdit) {
      if (this.props.attributeToEdit.vocabularyTerms && this.props.attributeToEdit.vocabularyTerms.length > 0) {
        const vocabularyTerms: IInputVocabularyTerm[] = this.props.attributeToEdit.vocabularyTerms.map(
          (v: VocabularyTerm) => {
            return { id: v.id, name: v.name, sortOrder: v.sortOrder };
          },
        );
        this.props.onControlledVocabularyListChanged(vocabularyTerms);
      }
    } else {
      // clear the redux state from the previous vocabulary terms settings
      this.props.onControlledVocabularyListChanged([]);
    }

    this.props
      .loadAttributes()
      .then((attributes) => {
        if (!this._isUnmounting) {
          const attributesUsedInFormula: Attribute[] = AddSampleAttributeImpl.filterAttributesForFormula(
            attributes,
            this.props.attributeToEdit,
          );
          const attributesUsedInSql: Attribute[] = AddSampleAttributeImpl.filterAttributesForDerivedValues(attributes);
          const attributesUsedInColoring: Attribute[] = AddSampleAttributeImpl.filterAttributesForColoring(attributes);

          this.setState({
            decorationAttributes: attributes,
            isLoadingAttributes: false,
            attributesUsedInFormula,
            attributesUsedInSql,
            attributesUsedInColoring,
          });
        }
      })
      .catch((error) => {
        this.setState({ dataFetchingWarning: "The loading of existing attributes failed" });
      });
  }

  componentDidUpdate(prevProps: IAddSampleAttributeDialogProps) {
    if (this.props.selectedAttributeTypeId !== prevProps.selectedAttributeTypeId) {
      this.adaptSettingsToAttributeType(this.props.selectedAttributeTypeId);
    }

    if (this.props.selectedAutomatic !== prevProps.selectedAutomatic) {
      this.adaptSettingsToAutomatic(this.props.selectedAutomatic);
    }

    if (this.props.selectedModifiable !== prevProps.selectedModifiable) {
      this.adaptSettingsToModifiable(this.props.selectedModifiable);
    }

    if (this.props.selectedMultipleValues !== prevProps.selectedMultipleValues) {
      this.adaptSettingsToMultipleValues(this.props.selectedMultipleValues);
    }

    if (this.props.enteredGroupName !== prevProps.enteredGroupName) {
      this.adaptSettingsToGroupName(this.props.enteredGroupName);
    }
  }

  handleSubmitData = (values): Promise<Attribute> => {
    // all values (except attributeType) are only included in the payload if they fit the selected attribute type
    const attributeType: ISelectOption =
      values[AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_ATTRIBUTE_TYPE];
    const attributeTypeId: number = attributeType?.value;

    if (attributeTypeId === ATTRIBUTE_TYPE.CONTROLLED_TEXT) {
      if (this.props.controlledValues.length === 0) {
        throw new SubmissionError({
          _error: `Please add some vocabulary terms for your new attribute of type 'Controlled Text'`,
        });
      }
    }

    const patternHint = trimOrThrow(
      this.getApplicableValue(
        values,
        AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_PATTERNHINT,
        attributeTypeId,
      ),
      null,
    );
    const pattern = trimOrThrow(
      this.getApplicableValue(
        values,
        AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_PATTERN,
        attributeTypeId,
      ),
      null,
    );
    if (patternHint != null && patternHint.trim().length > 0 && (pattern == null || pattern.trim().length === 0)) {
      throw new SubmissionError({ _error: `Text pattern cannot be empty when a text pattern hint is provided.` });
    }

    const selectedColumnSetId: ISelectOption =
      values[AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_COLUMN_SET];

    const payload: InputAttributeInput = {
      id: this.props.attributeToEdit ? this.props.attributeToEdit.id : null,
      attributeTypeId: attributeTypeId,
      fileTypeId:
        this.getApplicableValue(
          values,
          AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_FILE_TYPE,
          attributeTypeId,
        )?.value ?? null,
      automatic: this.getApplicableValue(
        values,
        AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_AUTOMATIC,
        attributeTypeId,
      ),
      columnSetId: selectedColumnSetId?.value,
      decimals:
        this.getApplicableValue(
          values,
          AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_DECIMALS,
          attributeTypeId,
        ) || null,
      disabled: this.getApplicableValue(
        values,
        AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_DISABLED,
        attributeTypeId,
      ),
      editOnce: this.getApplicableValue(
        values,
        AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_EDITONCE,
        attributeTypeId,
      ),
      multipleValues: this.getApplicableValue(
        values,
        AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_MULTIPLEVALUES,
        attributeTypeId,
      ),
      isHead: this.getApplicableValue(
        values,
        AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_IS_MULTIVALUES_HEAD,
        attributeTypeId,
      ),
      formulaText: trimOrThrow(
        this.getApplicableValue(
          values,
          AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_FORMULA,
          attributeTypeId,
        ),
        new SubmissionError({ _error: "Formula cannot be empty and must contain non white space characters" }),
      ),
      coloringRule: trimOrThrow(
        this.getApplicableValue(
          values,
          AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_COLORINGRULE,
          attributeTypeId,
        ),
        null,
      ),
      frozen: this.getApplicableValue(
        values,
        AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_FROZEN,
        attributeTypeId,
      ),
      groupName: trimOrThrow(
        this.getApplicableValue(
          values,
          AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_GROUPNAME,
          attributeTypeId,
        ),
        null,
      ),
      idAccessor: trimOrThrow(
        this.getApplicableValue(
          values,
          AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_IDACCESSOR,
          attributeTypeId,
        ),
        null,
      ),
      mandatory: this.getApplicableValue(
        values,
        AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_MANDATORY,
        attributeTypeId,
      ),
      maxLength:
        this.getApplicableValue(
          values,
          AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_MAXLENGTH,
          attributeTypeId,
        ) || null,
      maxValue:
        this.getApplicableValue(
          values,
          AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_MAX,
          attributeTypeId,
        ) || null,
      minValue:
        this.getApplicableValue(
          values,
          AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_MIN,
          attributeTypeId,
        ) || null,
      modifiable: this.getApplicableValue(
        values,
        AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_MODIFIABLE,
        attributeTypeId,
      ),
      name: trimOrThrow(
        this.getApplicableValue(
          values,
          AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_ATTRIBUTE_NAME,
          attributeTypeId,
        ),
        new SubmissionError({ _error: "Name value cannot be empty and must contain non white space characters" }),
      ),
      pattern: pattern,
      patternHint: patternHint,
      sqlStatement: this.getApplicableValue(
        values,
        AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_SQLSTATEMENT,
        attributeTypeId,
      ),
      vocabularyTerms:
        attributeTypeId === ATTRIBUTE_TYPE.CONTROLLED_TEXT
          ? this.props.controlledValues.map((controlledValue: IInputVocabularyTerm, index: number) => ({
              id: controlledValue.id,
              name: controlledValue.name,
              sortOrder: index,
            }))
          : undefined,
    };

    const handleSubmit = this.props.editingContext ? this.props.modifyAttribute : this.props.createAttribute;
    return handleSubmit(payload).then(
      (attribute: Attribute) => {
        this.props.onClose(attribute);
        this.props.reloadListOfAttributes();
        this.props.ensureRowIdIsVisible(attribute.id);

        return attribute;
      },
      // the following will throw an error that redux-form understands as a submission error, with a general error that will be
      // available from this.props.error
      (rejected: Error) => {
        throw new SubmissionError({ _error: rejected.message });
      },
    );
  };

  getApplicableValue = (values, field: string, attributeTypeId: number) => {
    let isApplicable = false;

    for (const key in AddSampleAttributeImpl.fieldVisibilitySettings) {
      if (
        AddSampleAttributeImpl.fieldVisibilitySettings.hasOwnProperty(key) &&
        AddSampleAttributeImpl.fieldVisibilitySettings[key].field === field
      ) {
        const attributeTypes = AddSampleAttributeImpl.fieldVisibilitySettings[key].attributeTypes;
        if (!attributeTypes || attributeTypes.length === 0 || attributeTypes.indexOf(attributeTypeId) > -1) {
          isApplicable = true;
          break;
        }
      }
    }
    if (
      field === AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_FROZEN &&
      attributeTypeId === ATTRIBUTE_TYPE.CONTROLLED_TEXT
    ) {
      return true;
    }
    // even if a field does not apply, the MT may expect a default setting, e.g. for the "frozen" field
    return isApplicable ? values[field] : AddSampleAttributeImpl.initialValues[field];
  };

  // Only analytical attributes can be calculated.
  // Only sample attributes can be files.
  // The following two functions are used to enforce valid selections.
  getCompatibleAttributeTypes = (): ISelectOption[] => {
    const attributeTypeOptions = ATTRIBUTE_TYPE_OPTIONS.filter((a: ISelectOption) => {
      return a.value !== ATTRIBUTE_TYPE.ORGANISM;
    });
    switch (this.props.selectedColumnSetId) {
      case COLUMN_SET.COS_ANALYTICAL_ID.id:
        return attributeTypeOptions;
      case COLUMN_SET.COS_ANNOTATION_ID.id:
        return attributeTypeOptions.filter((a: ISelectOption) => {
          return a.value !== ATTRIBUTE_TYPE.CALCULATED;
        });
      case COLUMN_SET.COS_PARENT_ID.id:
        return attributeTypeOptions.filter((a: ISelectOption) => {
          return a.value !== ATTRIBUTE_TYPE.CALCULATED && a.value !== ATTRIBUTE_TYPE.FILE;
        });
      default:
        return attributeTypeOptions;
    }
  };

  getCompatibleColumnSets = (): ISelectOption[] => {
    switch (this.props.selectedAttributeTypeId) {
      case ATTRIBUTE_TYPE.CALCULATED:
        return AddSampleAttributeImpl.columnSetOptions.filter((s: ISelectOption) => {
          return s.value === COLUMN_SET.COS_ANALYTICAL_ID.id;
        });
      case ATTRIBUTE_TYPE.FILE:
        return AddSampleAttributeImpl.columnSetOptions.filter((s: ISelectOption) => {
          return s.value === COLUMN_SET.COS_ANALYTICAL_ID.id || s.value === COLUMN_SET.COS_ANNOTATION_ID.id;
        });
      default:
        return AddSampleAttributeImpl.columnSetOptions;
    }
  };

  getCompatibleAttributeSuggestions = (attributes: Attribute[]): Attribute[] => {
    // parent attributes relate to parent attributes only, while sample attributes can relate to parent and sample attributes.
    return !this.props.selectedColumnSetId || this.props.selectedColumnSetId !== COLUMN_SET.COS_PARENT_ID.id
      ? attributes
      : attributes.filter((a: Attribute) => {
          return a.columnSet.id === COLUMN_SET.COS_PARENT_ID.id;
        });
  };

  getFooterMessage = (): {
    message?: ReactNode;
    intent?: TooltipIntent;
  } => {
    if (this.props.error) {
      return { message: this.props.error, intent: TooltipIntent.Danger };
    } else if (this.props.warning) {
      return { message: this.props.warning, intent: TooltipIntent.Warning };
    } else if (this.state.dataFetchingWarning) {
      return { message: this.state.dataFetchingWarning, intent: TooltipIntent.Warning };
    }
  };

  // Note that every adapt.. run will potentially trigger another one -
  // so make sure not to create a loop!
  adaptSettingsToAttributeType(selectedAttributeTypeId: number) {
    if (!AddSampleAttributeDialog.isAttributeTypeCompatibleWithMultiValues(this.props.selectedAttributeTypeId)) {
      this.props.changeFieldValue(
        AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_MULTIPLEVALUES,
        AddSampleAttributeImpl.initialValues[AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_MULTIPLEVALUES],
      );
    }

    if (this.isEvaluatedType(selectedAttributeTypeId)) {
      this.props.changeFieldValue(AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_AUTOMATIC, true);
    } else {
      if (!this.props.editingContext) {
        // apply the initial settings
        this.props.changeFieldValue(
          AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_MANDATORY,
          AddSampleAttributeImpl.initialValues[AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_MANDATORY],
        );
        this.props.changeFieldValue(
          AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_AUTOMATIC,
          AddSampleAttributeImpl.initialValues[AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_AUTOMATIC],
        );
        this.props.changeFieldValue(
          AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_EDITONCE,
          AddSampleAttributeImpl.initialValues[AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_EDITONCE],
        );
        this.props.changeFieldValue(
          AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_MODIFIABLE,
          AddSampleAttributeImpl.initialValues[AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_MODIFIABLE],
        );
        // the file type attributes must not be head
        this.props.changeFieldValue(
          AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_IS_MULTIVALUES_HEAD,
          AddSampleAttributeImpl.initialValues[
            AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_IS_MULTIVALUES_HEAD
          ],
        );
        // the file attributes should accept multiple values (= files) by default
        if (selectedAttributeTypeId === ATTRIBUTE_TYPE.FILE) {
          this.props.changeFieldValue(AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_MULTIPLEVALUES, true);
        }
      }
    }
  }

  adaptSettingsToAutomatic(selectedAutomatic: boolean) {
    if (selectedAutomatic) {
      this.props.changeFieldValue(AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_MODIFIABLE, true);
      this.props.changeFieldValue(AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_EDITONCE, false);
      this.props.changeFieldValue(AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_MANDATORY, false);
    }
  }

  adaptSettingsToModifiable(selectedModifiable: boolean) {
    if (!selectedModifiable) {
      this.props.changeFieldValue(AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_EDITONCE, false);
      this.props.changeFieldValue(AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_MANDATORY, false);
    }
  }

  adaptSettingsToMultipleValues(selectedMultipleValue: boolean) {
    if (!selectedMultipleValue) {
      this.props.changeFieldValue(AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_IS_MULTIVALUES_HEAD, false);
    }
    if (selectedMultipleValue) {
      this.props.changeFieldValue(AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_COLORINGRULE, null);
    }
  }

  adaptSettingsToGroupName(groupName: string) {
    if (!groupName) {
      this.props.changeFieldValue(AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_IS_MULTIVALUES_HEAD, false);
    }
  }

  static isAttributeTypeCompatibleWithMultiValues(attributeTypeId: number) {
    return (
      attributeTypeId !== ATTRIBUTE_TYPE.CALCULATED &&
      attributeTypeId !== ATTRIBUTE_TYPE.DYNAMIC_TERMS &&
      attributeTypeId !== ATTRIBUTE_TYPE.DERIVED_TERMS
    );
  }

  render() {
    const mandatoryDisabled: boolean =
      this.isEvaluatedType(this.props.selectedAttributeTypeId) ||
      this.props.selectedAutomatic ||
      !this.props.selectedModifiable;
    const mandatoryTooltip: string = mandatoryDisabled
      ? "This setting is disabled for calculated, derived and automatic attributes and if the attribute is not modifiable"
      : null;

    const automaticDisabled: boolean =
      this.isEvaluatedType(this.props.selectedAttributeTypeId) ||
      this.props.selectedAttributeTypeId === ATTRIBUTE_TYPE.DYNAMIC_TERMS || // SEL6-1352: MT currently does not support these as automatic
      this.props.selectedAttributeTypeId === ATTRIBUTE_TYPE.HYPERLINK || // SEL6-1352: MT currently does not support these as automatic
      (this.props.attributeToEdit && isAutomaticRepresenterAttribute(this.props.attributeToEdit)) ||
      // although the automatic setting is generally not mutable, we need an exception for the parent names
      // (otherwise it is not possible to configure automatic parent name generation)
      (this.props.editingContext &&
        !(
          this.props.attributeToEdit &&
          this.props.attributeToEdit.columnSet.id === COLUMN_SET.COS_PARENT_ID.id &&
          this.props.attributeToEdit.representedColumn === REPRESENTER_COLUMN.NAME
        ));
    const automaticTooltip: string = automaticDisabled
      ? "This setting is disabled when modifying an existing attribute, for dynamic terms, hyperlinks, calculated and derived attributes and some attributes with a special meaning"
      : null;

    const modifiableDisabled: boolean =
      // Note: With the meaning change of "not modifiable" to "not modifiable by user, but modifiable by application incl. Rest API"
      // name representer attributes (parent and sample name) can be set to "not modifiable".
      // This allows to prevent manual changes of these names.
      this.isEvaluatedType(this.props.selectedAttributeTypeId) || this.props.selectedAutomatic;
    const modifiableTooltip: string = modifiableDisabled
      ? "This setting is disabled for calculated, derived and automatic attributes"
      : null;

    const editOnceDisabled: boolean =
      this.isEvaluatedType(this.props.selectedAttributeTypeId) ||
      this.props.selectedAutomatic ||
      !this.props.selectedModifiable;

    const editOnceTooltip: string = editOnceDisabled
      ? "This setting is disabled for calculated, derived, automatic and not modifiable attributes"
      : null;

    const disabledDisabled: boolean =
      this.props.attributeToEdit &&
      (this.props.attributeToEdit.referencedBy.length > 0 || isRepresenterOrUsageAttribute(this.props.attributeToEdit));
    const disabledTooltip: string = disabledDisabled
      ? "This setting is disabled because the attribute is used in a calculated attribute or it has a special meaning"
      : null;

    const multipleValuesDisabled: boolean =
      !AddSampleAttributeDialog.isAttributeTypeCompatibleWithMultiValues(this.props.selectedAttributeTypeId) ||
      (this.props.attributeToEdit &&
        (this.props.attributeToEdit.referencedBy.length > 0 ||
          isRepresenterOrUsageAttribute(this.props.attributeToEdit) ||
          isAutomaticRepresenterAttribute(this.props.attributeToEdit) ||
          (this.props.attributeToEdit.multipleValues && this.props.attributeToEdit.hasData)));
    const multipleValuesTooltip: string = multipleValuesDisabled
      ? "This setting is disabled for calculated, dynamic term and derived value attribute types, or because the attribute has a special meaning, is used in a calculated attribute, or is already associated with multiple values data."
      : null;
    const isHeadDisabled: boolean =
      !this.props.selectedMultipleValues ||
      this.props.selectedAttributeTypeId === ATTRIBUTE_TYPE.FILE ||
      !this.props.enteredGroupName;
    const isHeadTooltip: string = isHeadDisabled
      ? "This setting is active for multiple value attributes if a group name was entered. File attributes can't be set as head."
      : null;

    const columnSetDisabled: boolean =
      this.props.attributeToEdit &&
      (this.props.attributeToEdit.hasData ||
        !!this.props.attributeToEdit.parentType ||
        this.props.attributeToEdit.sampleTypes.length > 0 ||
        isAutomaticRepresenterAttribute(this.props.attributeToEdit));
    const columnSetTooltip: string = columnSetDisabled
      ? "This setting is disabled because there is data associated with the attribute or it is used either in parent types or in sample types"
      : null;

    const attributeTypeDisabled: boolean = this.props.editingContext;
    const fileTypeDisabled: boolean = this.props.editingContext;
    const attributeTypeTooltip: string = attributeTypeDisabled
      ? "This setting is disabled because it is not possible to modify the attribute type after attribute creation"
      : null;

    const sqlStatementDynamicDisabled: boolean = this.props.attributeToEdit?.hasData;
    const sqlStatementDynamicTooltip: string = sqlStatementDynamicDisabled
      ? "This setting is disabled because there is data associated with the attribute"
      : null;

    const idAccessorDisabled: boolean = this.props.attributeToEdit && this.props.attributeToEdit.hasData;
    const idAccessorTooltip: string = idAccessorDisabled
      ? "This setting is disabled because there is data associated with the attribute"
      : null;

    const maxLengthDisabled: boolean =
      this.props.attributeToEdit && isAutomaticRepresenterAttribute(this.props.attributeToEdit);
    const maxLengthTooltip: string = maxLengthDisabled
      ? "This setting is disabled for automatic attributes with a special meaning"
      : null;

    const textPatternDisabled: boolean =
      this.props.attributeToEdit && isAutomaticRepresenterAttribute(this.props.attributeToEdit);
    const textPatternTooltip: string = textPatternDisabled
      ? "This setting is disabled for automatic attributes with a special meaning"
      : null;
    const fileTypeTooltip: string = fileTypeDisabled
      ? "This setting is disabled because it is not possible to modify the file type after attribute creation"
      : "Select which type of files can be stored with this attribute.";

    const coloringRuleDisabled: boolean =
      // if multi value
      this.props.selectedMultipleValues ||
      // if a representer attribute (name, description, status...)
      !!this.props.attributeToEdit?.representedColumn;
    const coloringRuleTooltip: string = coloringRuleDisabled
      ? "This setting is disabled for multi-value attributes and for attributes with a special meaning"
      : null;

    return (
      <Dialog
        mainActionButtonProps={{
          children: "Save",
          isLoading: this.props.submitting,
          isDisabled: this.props.submitting,
          onPress: this.props.handleSubmit(this.handleSubmitData),
        }}
        headerProps={{ title: this.props.editingContext ? "Attribute Details" : "New Attribute" }}
        closeFn={this.props.onClose}
        footerMessage={this.getFooterMessage()}
        width={600}
      >
        <Form
          className={"create-attribute-form"}
          onSubmit={() => {
            /*empty*/
          }}
        >
          <span>Please provide the following information about the attribute.</span>
          <br />
          <br />

          {this.props.editingContext && this.state.isLoadingAttributes ? (
            <LoadingSpinner key="tabbed-table-view-spinner" />
          ) : (
            <SelForm>
              <InputReduxFormField
                componentClass="input"
                name={this.getFieldFormField(this.ATTRIBUTE_NAME_FIELD)}
                label={<FieldTitle label={"Attribute name"} mandatory />}
                placeholder={`Enter sample attribute name`}
                maxLength={DB_FIELD_MAX_LENGTH.ATTRIBUTE.NAME}
                component={InputField}
                validate={this.validateAttributeName}
              />

              <InputReduxFormField
                componentClass="input"
                name={this.getFieldFormField(this.GROUP_NAME_FIELD)}
                label={
                  <HStack>
                    Group name <InfoToggletip content={this.getFieldHint(this.GROUP_NAME_FIELD)} />
                  </HStack>
                }
                placeholder={`Enter attribute group name`}
                maxLength={DB_FIELD_MAX_LENGTH.ATTRIBUTE.GROUP_NAME}
                component={InputField}
              />

              <ReactSelectReduxFormField
                name={this.getFieldFormField(this.COLUMN_SET_FIELD)}
                label={
                  <FieldTitle
                    label={"Column set"}
                    mandatory
                    rightElement={<InfoToggletip content={this.getFieldHint(this.COLUMN_SET_FIELD)} />}
                  />
                }
                component={ReactSelectField}
                options={this.getCompatibleColumnSets()}
                isSearchable
                closeMenuOnSelect
                openMenuOnFocus
                isClearable={true}
                validate={simpleDropdownFieldValidation}
                isDisabled={columnSetDisabled}
                tooltip={columnSetTooltip}
              />

              <ReactSelectReduxFormField
                name={this.getFieldFormField(this.ATTRIBUTE_TYPE_FIELD)}
                label={<FieldTitle label={"Attribute type"} mandatory />}
                component={ReactSelectField}
                options={this.getCompatibleAttributeTypes()}
                isSearchable
                closeMenuOnSelect
                openMenuOnFocus
                isClearable={true}
                validate={simpleDropdownFieldValidation}
                isDisabled={attributeTypeDisabled}
                tooltip={attributeTypeTooltip}
                maxMenuHeight={200}
              />
              {this.props.selectedAttributeTypeId && (
                <>
                  <Collapse
                    isExpanded={this.isSelectedAttributeOneOf(
                      AddSampleAttributeImpl.getFieldAttributeTypes(this.FILE_TYPE_FIELD),
                    )}
                    unmountOnExit={true}
                  >
                    <div>
                      <ReactSelectReduxFormField
                        name={this.getFieldFormField(this.FILE_TYPE_FIELD)}
                        label={<FieldTitle label={"File type"} mandatory />}
                        component={ReactSelectField}
                        options={SAM_FILE_TYPE_OPTIONS}
                        isSearchable
                        closeMenuOnSelect
                        openMenuOnFocus
                        isClearable={true}
                        isDisabled={fileTypeDisabled}
                        validate={simpleDropdownFieldValidation}
                        tooltip={fileTypeTooltip}
                        maxMenuHeight={200}
                      />
                    </div>
                  </Collapse>

                  <div className={style.add_attribute_form_flags_content}>
                    <CheckboxReduxFormField
                      name={this.getFieldFormField(this.MANDATORY_FIELD)}
                      formGroupClassName={style.add_attribute_form_flag}
                      label={
                        <span>
                          Mandatory <InfoToggletip content={this.getFieldHint(this.MANDATORY_FIELD)} />
                        </span>
                      }
                      component={CheckboxField}
                      disabled={mandatoryDisabled}
                      tooltipText={mandatoryTooltip}
                    />

                    <CheckboxReduxFormField
                      name={this.getFieldFormField(this.DISABLED_FIELD)}
                      formGroupClassName={style.add_attribute_form_flag}
                      label={
                        <span>
                          Disabled <InfoToggletip content={this.getFieldHint(this.DISABLED_FIELD)} />
                        </span>
                      }
                      component={CheckboxField}
                      disabled={disabledDisabled}
                      tooltipText={disabledTooltip}
                    />

                    <CheckboxReduxFormField
                      name={this.getFieldFormField(this.AUTOMATIC_FIELD)}
                      formGroupClassName={style.add_attribute_form_flag}
                      label={
                        <span>
                          Automatic <InfoToggletip content={this.getFieldHint(this.AUTOMATIC_FIELD)} />
                        </span>
                      }
                      component={CheckboxField}
                      disabled={automaticDisabled}
                      tooltipText={automaticTooltip}
                    />

                    <CheckboxReduxFormField
                      name={this.getFieldFormField(this.MODIFIABLE_FIELD)}
                      formGroupClassName={style.add_attribute_form_flag}
                      label={
                        <span>
                          Modifiable <InfoToggletip content={this.getFieldHint(this.MODIFIABLE_FIELD)} />
                        </span>
                      }
                      component={CheckboxField}
                      disabled={modifiableDisabled}
                      tooltipText={modifiableTooltip}
                    />

                    <CheckboxReduxFormField
                      name={this.getFieldFormField(this.EDIT_ONCE_FIELD)}
                      formGroupClassName={style.add_attribute_form_flag}
                      label={
                        <span>
                          Edit once <InfoToggletip content={this.getFieldHint(this.EDIT_ONCE_FIELD)} />
                        </span>
                      }
                      component={CheckboxField}
                      disabled={editOnceDisabled}
                      tooltipText={editOnceTooltip}
                    />

                    <CheckboxReduxFormField
                      name={this.getFieldFormField(this.MULTIPLE_VALUES_FIELD)}
                      formGroupClassName={style.add_attribute_form_flag}
                      label={
                        <span>
                          Multiple values
                          <InfoToggletip content={this.getFieldHint(this.MULTIPLE_VALUES_FIELD)} />
                        </span>
                      }
                      component={CheckboxField}
                      disabled={multipleValuesDisabled}
                      tooltipText={multipleValuesTooltip}
                    />

                    <CheckboxReduxFormField
                      name={this.getFieldFormField(this.IS_HEAD_FIELD)}
                      formGroupClassName={style.add_attribute_form_flag}
                      label={
                        <span>
                          Is multi values head
                          <InfoToggletip content={this.getFieldHint(this.IS_HEAD_FIELD)} />
                        </span>
                      }
                      component={CheckboxField}
                      disabled={isHeadDisabled}
                      tooltipText={isHeadTooltip}
                    />
                  </div>

                  <Collapse
                    isExpanded={this.isSelectedAttributeOneOf(
                      AddSampleAttributeImpl.getFieldAttributeTypes(this.MIN_FIELD),
                    )}
                    unmountOnExit={true}
                  >
                    <div>
                      <InputReduxFormField
                        componentClass="input"
                        name={this.getFieldFormField(this.MIN_FIELD)}
                        label={"Min value"}
                        type={"number"}
                        parse={(value) => Number(value)}
                        component={InputField}
                        validate={this.validateMinMaxValues}
                      />
                    </div>
                  </Collapse>

                  <Collapse
                    isExpanded={this.isSelectedAttributeOneOf(
                      AddSampleAttributeImpl.getFieldAttributeTypes(this.MAX_FIELD),
                    )}
                    unmountOnExit={true}
                  >
                    <div>
                      <InputReduxFormField
                        componentClass="input"
                        name={this.getFieldFormField(this.MAX_FIELD)}
                        label={"Max value"}
                        type={"number"}
                        parse={(value) => Number(value)}
                        component={InputField}
                        validate={this.validateMinMaxValues}
                      />
                    </div>
                  </Collapse>

                  <Collapse
                    isExpanded={this.isSelectedAttributeOneOf(
                      AddSampleAttributeImpl.getFieldAttributeTypes(this.DECIMALS_FIELD),
                    )}
                    unmountOnExit={true}
                  >
                    <div>
                      <InputReduxFormField
                        componentClass="input"
                        name={this.getFieldFormField(this.DECIMALS_FIELD)}
                        label={
                          <span>
                            Decimals <InfoToggletip content={this.getFieldHint(this.DECIMALS_FIELD)} />
                          </span>
                        }
                        type={"number"}
                        parse={(value) => Number(value)}
                        component={InputField}
                        validate={this.validateDecimals}
                      />
                    </div>
                  </Collapse>

                  <Collapse
                    isExpanded={this.isSelectedAttributeOneOf(
                      AddSampleAttributeImpl.getFieldAttributeTypes(this.MAX_LENGTH_FIELD),
                    )}
                    unmountOnExit={true}
                  >
                    <div>
                      <InputReduxFormField
                        componentClass="input"
                        name={this.getFieldFormField(this.MAX_LENGTH_FIELD)}
                        label={"Max length"}
                        type={"number"}
                        parse={(value) => Number(value)}
                        component={InputField}
                        validate={this.validateMaxLength}
                        disabled={maxLengthDisabled}
                        tooltip={maxLengthTooltip}
                      />
                    </div>
                  </Collapse>

                  <Collapse
                    isExpanded={this.isSelectedAttributeOneOf(
                      AddSampleAttributeImpl.getFieldAttributeTypes(this.PATTERN_FIELD),
                    )}
                    unmountOnExit={true}
                  >
                    <div>
                      <InputReduxFormField
                        componentClass="input"
                        name={this.getFieldFormField(this.PATTERN_FIELD)}
                        label={
                          <span>
                            Text pattern <InfoToggletip content={this.getFieldHint(this.PATTERN_FIELD)} />
                          </span>
                        }
                        maxLength={DB_FIELD_MAX_LENGTH.ATTRIBUTE.PATTERN}
                        component={InputField}
                        validate={this.validateRegExpPattern}
                        disabled={textPatternDisabled}
                        tooltip={textPatternTooltip}
                      />
                    </div>
                  </Collapse>

                  <Collapse
                    isExpanded={this.isSelectedAttributeOneOf(
                      AddSampleAttributeImpl.getFieldAttributeTypes(this.PATTERN_HINT_FIELD),
                    )}
                    unmountOnExit={true}
                  >
                    <div>
                      <InputReduxFormField
                        componentClass="input"
                        name={this.getFieldFormField(this.PATTERN_HINT_FIELD)}
                        label={"Text pattern hint"}
                        maxLength={DB_FIELD_MAX_LENGTH.ATTRIBUTE.PATTERN_HINT}
                        component={InputField}
                        disabled={textPatternDisabled}
                        tooltip={textPatternTooltip}
                      />
                    </div>
                  </Collapse>

                  <Collapse
                    isExpanded={this.isSelectedAttributeOneOf(
                      AddSampleAttributeImpl.getFieldAttributeTypes(AddSampleAttributeImpl.FORMULA_FIELD),
                    )}
                    unmountOnExit={true}
                  >
                    <div>
                      <ScriptInputReduxFormField
                        name={this.getFieldFormField(AddSampleAttributeImpl.FORMULA_FIELD)}
                        title={
                          <FieldTitle
                            label={"Formula"}
                            mandatory
                            rightElement={
                              <InfoToggletip content={this.getFieldHint(AddSampleAttributeImpl.FORMULA_FIELD)} />
                            }
                          />
                        }
                        isLoading={this.state.isLoadingAttributes}
                        maxLength={DB_FIELD_MAX_LENGTH.ATTRIBUTE.FORMULA}
                        attributes={this.state.attributesUsedInFormula}
                        validate={[simpleFieldValidation]}
                        scriptingLanguage={ScriptingLanguage.JAVASCRIPT}
                      />
                    </div>
                  </Collapse>

                  <Collapse
                    isExpanded={this.isSelectedAttributeOneOf(
                      AddSampleAttributeImpl.getFieldAttributeTypes(AddSampleAttributeImpl.COLORING_RULE_FIELD),
                    )}
                    unmountOnExit={true}
                  >
                    <div>
                      <ScriptInputReduxFormField
                        name={this.getFieldFormField(AddSampleAttributeImpl.COLORING_RULE_FIELD)}
                        title={
                          <FieldTitle
                            label={
                              <span>
                                Coloring rule{" "}
                                <InfoToggletip
                                  content={this.getFieldHint(AddSampleAttributeImpl.COLORING_RULE_FIELD)}
                                />
                              </span>
                            }
                          />
                        }
                        isLoading={this.state.isLoadingAttributes}
                        maxLength={DB_FIELD_MAX_LENGTH.ATTRIBUTE.COLORING_RULE}
                        attributes={this.getCompatibleAttributeSuggestions(this.state.attributesUsedInColoring)}
                        disabled={coloringRuleDisabled}
                        tooltip={coloringRuleTooltip}
                        scriptingLanguage={ScriptingLanguage.JAVASCRIPT}
                        acceptedMacros={[SCRIPTING_MACRO.SELF_VALUE]}
                      />
                    </div>
                  </Collapse>

                  <Collapse
                    isExpanded={this.isSelectedAttributeOneOf(
                      AddSampleAttributeImpl.getFieldAttributeTypes(this.DYNAMIC_SQL_FIELD),
                    )}
                    unmountOnExit={true}
                  >
                    <div>
                      <ScriptInputReduxFormField
                        name={this.getFieldFormField(this.DYNAMIC_SQL_FIELD)}
                        title={
                          <FieldTitle
                            label={
                              <span>
                                SQL statement <InfoToggletip content={this.getFieldHint(this.DYNAMIC_SQL_FIELD)} />
                              </span>
                            }
                            mandatory
                          />
                        }
                        tooltip={sqlStatementDynamicTooltip}
                        maxLength={DB_FIELD_MAX_LENGTH.ATTRIBUTE.SQL_STATEMENT}
                        validate={simpleFieldValidation}
                        disabled={sqlStatementDynamicDisabled}
                        scriptingLanguage={ScriptingLanguage.SQL}
                      />
                    </div>
                  </Collapse>

                  <Collapse
                    isExpanded={this.isSelectedAttributeOneOf(
                      AddSampleAttributeImpl.getFieldAttributeTypes(this.DERIVED_SQL_FIELD),
                    )}
                    unmountOnExit={true}
                  >
                    <div>
                      <ScriptInputReduxFormField
                        name={this.getFieldFormField(this.DERIVED_SQL_FIELD)}
                        title={
                          <FieldTitle
                            label={
                              <span>
                                SQL statement <InfoToggletip content={this.getFieldHint(this.DERIVED_SQL_FIELD)} />
                              </span>
                            }
                            mandatory
                          />
                        }
                        maxLength={DB_FIELD_MAX_LENGTH.ATTRIBUTE.SQL_STATEMENT}
                        isLoading={this.state.isLoadingAttributes}
                        attributes={this.getCompatibleAttributeSuggestions(this.state.attributesUsedInSql)}
                        validate={[simpleFieldValidation]}
                        isEmbeddedAttributeValid={this.validateDerivedValue}
                        scriptingLanguage={ScriptingLanguage.SQL}
                      />
                    </div>
                  </Collapse>

                  <Collapse
                    isExpanded={this.isSelectedAttributeOneOf(
                      AddSampleAttributeImpl.getFieldAttributeTypes(this.ID_ACCESSOR_FIELD),
                    )}
                    unmountOnExit={true}
                  >
                    <div>
                      <InputReduxFormField
                        componentClass="input"
                        name={this.getFieldFormField(this.ID_ACCESSOR_FIELD)}
                        label={
                          <FieldTitle
                            label={
                              <span>
                                ID accessor <InfoToggletip content={this.getFieldHint(this.ID_ACCESSOR_FIELD)} />
                              </span>
                            }
                            mandatory
                          />
                        }
                        component={InputField}
                        maxLength={DB_FIELD_MAX_LENGTH.ATTRIBUTE.IDACCESSOR}
                        validate={simpleFieldValidation}
                        placeholder={'"SCHEMA"."TABLE"."COLUMN_ID"'}
                        disabled={idAccessorDisabled}
                        tooltip={idAccessorTooltip}
                      />
                    </div>
                  </Collapse>
                  {/* Keep the following code, perhaps the frozen/non-frozen functionality will be implemented at some point. */}
                  {/*<Collapse*/}
                  {/*  isExpanded={this.isSelectedAttributeOneOf(AddSampleAttributeImpl.getFieldAttributeTypes(this.FROZEN_FIELD))}*/}
                  {/*  >*/}
                  {/*  <div>*/}
                  {/*    <CheckboxReduxFormField*/}
                  {/*      name={this.getFieldFormField(this.FROZEN_FIELD)}*/}
                  {/*      title={null}*/}
                  {/*      leftLabel={<span>Frozen <InfoIconTooltipSwitcher*/}
                  {/*        tooltipContent={this.getFieldHint(this.FROZEN_FIELD)}/></span>}*/}
                  {/*      horizontalLayout={true}*/}
                  {/*      component={CheckboxField}*/}
                  {/*    />*/}
                  {/*  </div>*/}
                  {/*</Collapse>*/}

                  <Collapse
                    isExpanded={this.isSelectedAttributeOneOf(
                      AddSampleAttributeImpl.getFieldAttributeTypes(this.VOCABULARY_TERMS_FIELD),
                    )}
                    unmountOnExit={true}
                  >
                    <div>
                      <ControlledValuesManager
                        controlledValues={this.props.controlledValues}
                        handleControlledValuesChanged={this.props.onControlledVocabularyListChanged}
                        editingContext={this.props.editingContext}
                        label={"Vocabulary terms"}
                        maxTermLength={DB_FIELD_MAX_LENGTH.ATTRIBUTE.TERM}
                      />
                    </div>
                  </Collapse>
                </>
              )}
            </SelForm>
          )}
        </Form>
      </Dialog>
    );
  }

  isSelectedAttributeOneOf = (attributeTypes: number[]) => {
    return !!attributeTypes.find((typeId: number) => this.props.selectedAttributeTypeId === typeId);
  };

  isInteger = (value: string): boolean => {
    const v = Number(value);

    return !Number.isNaN(v) && Number.isInteger(v);
  };

  validateDecimals = (value) => {
    if (value && !this.isInteger(value)) {
      return "Integer value expected";
    }
    if (Number(value) < 0) {
      return "The number of decimals cannot be negative";
    } else if (Number(value) > 38) {
      return "The number of decimals cannot be beyond 38";
    }
  };

  validateMaxLength = (value) =>
    value &&
    (Number(value) <= 0 || Number(value) > DB_FIELD_MAX_LENGTH.ATTRIBUTE.VALUE) &&
    `The maximum length of the attribute value should be between 1 and ${DB_FIELD_MAX_LENGTH.ATTRIBUTE.VALUE}`;

  validateMinMaxValues = (value, allValues) => {
    if (value && Number.isNaN(Number(value))) {
      return "Invalid number";
    }

    const minValue = Number(allValues[AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_MIN]);
    const maxValue = Number(allValues[AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_MAX]);

    if (minValue && maxValue && minValue > maxValue) {
      return "Min value cannot be above max value";
    }
  };

  validateAttributeName = (value) => {
    if (!value || value.trim().length === 0) {
      return `Your input is required.`;
    }
    if (!isNaN(value)) {
      return `The attribute name should contain at least one alphabetical character.`;
    }
  };

  validateRegExpPattern = (value) => {
    try {
      new RegExp(value);
    } catch (error) {
      return error.message;
    }
  };

  validateDerivedValue = (embeddedAttributeId: number) => {
    const validAttribute = this.state.attributesUsedInSql.find((attribute) => attribute.id === embeddedAttributeId);
    if (!validAttribute) {
      return `Attribute '${embeddedAttributeId}' is unknown or is not of type Dynamic Term.`;
    }
    if (
      this.props.selectedColumnSetId === COLUMN_SET.COS_PARENT_ID.id &&
      validAttribute.columnSet.id !== COLUMN_SET.COS_PARENT_ID.id
    ) {
      // Derived Values in Parents must not relate to Dynamic Terms in Samples, because 1 parent would have to relate to many sample rows.
      return `Attribute '${validAttribute.attributeName}' is a Dynamic Term for a Sample. Derived Values in Parent attributes must relate to Dynamic Terms in the same Parent.`;
    }
  };

  static warnFormulaModification = (_value, props: IAddSampleAttributeDialogProps): FormWarnings<any> => {
    if (props.editingContext) {
      const formulaTouched: boolean =
        props.fieldState &&
        props.fieldState[AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_FORMULA] &&
        props.fieldState[AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_DIALOG_FIELD_FORMULA].touched;
      if (formulaTouched) {
        return { _warning: "Changing the formula may take a while depending on the number of samples affected" };
      }
    }
  };
}

const AddSampleAttributeDialog = connect<
  IAddSampleAttributeDialogAppStateProps,
  IAddSampleAttributeDialogDispatchProps,
  IAddSampleAttributeDialogOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: AddSampleAttributeImpl.ADD_SAMPLE_ATTRIBUTE_FORM_NAME,
    warn: AddSampleAttributeImpl.warnFormulaModification,
    touchOnChange: true, // so that the warn above can be shown on formula modification
  })(AddSampleAttributeImpl) as any,
);

export { AddSampleAttributeImpl };
export default AddSampleAttributeDialog;
